import type {
  AddCase,
  AddCaseParams,
  AddCaseQuery,
  AddCaseQueryParams,
  ConfirmCaseFilesUploaded,
  DeleteCase,
  DeleteCaseParams,
  EditCaseQuery,
  EditCaseQueryParams,
  GetCase,
  GetCaseParams,
  ListCases,
  ListCasesParams,
  MarkCaseAsRead,
  RemoveCaseFile,
  StartCaseFileUpload,
  UpdateCase,
  UpdateCaseParams,
} from '@/backend/caseTypes'
import { get, post, put, remove } from './client'

export const addCase = async ({ name, workspaceId }: AddCaseParams) =>
  post<AddCase['body'], AddCase['successResponse'], AddCase['path']>(
    `/api/workspaces/${workspaceId}/cases`,
    { name },
  )

export const addCaseQuery = async ({
  caseId,
  fileIds,
  text,
  workspaceId,
  agentId,
  hubId,
  webSearch,
}: AddCaseQueryParams) =>
  post<AddCaseQuery['body'], AddCaseQuery['successResponse'], AddCaseQuery['path']>(
    `/api/workspaces/${workspaceId}/cases/${caseId}/queries`,
    {
      /**
       * We will receive 400 errors if we send both an agent and request a web
       * search. This is the most robust way to ensure we never send both.
       */
      agent_id: webSearch ? undefined : agentId,
      file_ids: fileIds,
      text,
      search_web: webSearch || false,
      hub_id: hubId,
    },
  )

export const deleteCase = async ({ caseId, workspaceId }: DeleteCaseParams) =>
  remove<undefined, DeleteCase['successResponse'], DeleteCase['path']>(
    `/api/workspaces/${workspaceId}/cases/${caseId}`,
    undefined,
  )

export const editCaseQuery = async ({
  agentId,
  caseId,
  fileIds,
  hubId,
  messageId,
  text,
  workspaceId,
  webSearch,
}: EditCaseQueryParams) =>
  put<EditCaseQuery['body'], EditCaseQuery['successResponse'], EditCaseQuery['path']>(
    `/api/workspaces/${workspaceId}/cases/${caseId}/queries/${messageId}`,
    {
      /**
       * We will receive 400 errors if we send both an agent and request a web
       * search. This is the most robust way to ensure we never send both.
       */
      agent_id: webSearch ? undefined : agentId,
      file_ids: fileIds,
      text,
      search_web: webSearch || false,
      hub_id: hubId,
    },
  )

export const listCases = async ({
  workspaceId,
  after,
  before,
  first,
  last,
  name_contains,
  order,
  owner_ids,
  offset,
  limit,
}: ListCasesParams) =>
  get<ListCases['queryParams'], ListCases['successResponse'], ListCases['path']>(
    `/api/workspaces/${workspaceId}/cases`,
    {
      after,
      before,
      first,
      last,
      name_contains,
      owner_ids: owner_ids ?? undefined,
      order_by: order?.map(([orderBy]) => orderBy),
      order_directions: order?.map(([, orderDirection]) => orderDirection),
      offset,
      limit,
    },
  )

/**
 * Fires a network request to he backend to provide a file url to upload a file to
 * The backend action will generate a presigned url for the file and return it,
 * and also save it onto the manual_value of the given field.
 */
export const confirmCaseFilesUploaded = (workspaceId: string, caseId: string, fileIds: string[]) =>
  post<
    ConfirmCaseFilesUploaded['body'],
    ConfirmCaseFilesUploaded['successResponse'],
    ConfirmCaseFilesUploaded['path']
  >(`/api/workspaces/${workspaceId}/cases/${caseId}/confirm_files_uploaded`, { file_ids: fileIds })

export const getCase = async ({ caseId, workspaceId }: GetCaseParams) =>
  get<undefined, GetCase['successResponse'], GetCase['path']>(
    `/api/workspaces/${workspaceId}/cases/${caseId}`,
    undefined,
  )

/**
 * Remove a file from a case.
 * Only files in "errored" or "uploading" state can be removed.
 * There is an ongoing discussion whether we should allow this for all,
 * or at least all orphaned files.
 */
export const removeCaseFile = (workspaceId: string, caseId: string, fileId: string) =>
  remove<RemoveCaseFile['body'], RemoveCaseFile['successResponse'], RemoveCaseFile['path']>(
    `/api/workspaces/${workspaceId}/cases/${caseId}/files/${fileId}`,
    undefined,
  )

/**
 * Fires a network request to he backend to provide a file url to upload a file to
 * The backend action will generate a presigned url for the file and return it,
 * and also save it onto the manual_value of the given field.
 */
export const startCaseFileUpload = (workspaceId: string, caseId: string, filename: string) =>
  post<
    StartCaseFileUpload['body'],
    StartCaseFileUpload['successResponse'],
    StartCaseFileUpload['path']
  >(`/api/workspaces/${workspaceId}/cases/${caseId}/start_file_upload`, { filename })

export const updateCase = async ({ caseId, workspaceId, name }: UpdateCaseParams) =>
  post<UpdateCase['body'], UpdateCase['successResponse'], UpdateCase['path']>(
    `/api/workspaces/${workspaceId}/cases/${caseId}`,
    { name },
  )

export const markCaseAsRead = async ({
  caseId,
  workspaceId,
}: {
  caseId: string
  workspaceId: string
}) =>
  post<MarkCaseAsRead['body'], MarkCaseAsRead['successResponse'], MarkCaseAsRead['path']>(
    `/api/workspaces/${workspaceId}/cases/${caseId}/mark_as_read`,
    undefined,
  )
